import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { connectSocket, disconnectSocket, getSocket } from '../../services/socket';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button,
  Switch, FormControlLabel, Chip, Alert, Avatar, Divider, Paper,
  LinearProgress, IconButton, CircularProgress,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NavigationIcon from '@mui/icons-material/Navigation';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallIcon from '@mui/icons-material/Call';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StripeConnectSection from '../../components/StripeConnectSection';

const fmt = (v) => parseFloat(v || 0).toFixed(2);

const VTCPartner = () => {
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(false);
  const [stats, setStats] = useState({ today_rides: 0, today_earnings: '0.00', total_rides: 0, rating: '—' });
  const [currentRide, setCurrentRide] = useState(null);
  const [pendingRides, setPendingRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const watchIdRef = useRef(null);
  const pollRef = useRef(null);

  const loadDriverData = useCallback(async () => {
    try {
      const [statsRes, currentRes, pendingRes] = await Promise.all([
        api.get('/vtc/driver/stats'),
        api.get('/vtc/driver/current'),
        api.get('/vtc/driver/pending'),
      ]);
      setStats(statsRes.data.stats || {});
      setCurrentRide(currentRes.data.ride || null);
      setPendingRides(pendingRes.data.rides || []);
    } catch {
      // profil chauffeur peut ne pas exister encore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const s = connectSocket(user.id);

    // Nouvelle course disponible → rafraîchir la liste
    s.on('vtc-new-ride', (ride) => {
      setPendingRides(prev => {
        if (prev.find(r => r.id === ride.id)) return prev;
        return [ride, ...prev];
      });
    });

    loadDriverData();
    // Fallback poll toutes les 30s si socket déconnecté
    pollRef.current = setInterval(() => {
      if (!getSocket()?.connected) loadDriverData();
    }, 30000);

    return () => {
      clearInterval(pollRef.current);
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      s.off('vtc-new-ride');
      disconnectSocket();
    };
  }, [loadDriverData]);

  const startLocationTracking = () => {
    if (!navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        // REST pour persister en DB
        api.put('/vtc/drivers/location', { latitude, longitude }).catch(() => {});
        // Socket pour envoyer en temps réel au client de la course active
        if (currentRide) {
          getSocket()?.emit('vtc-location-update', { rideId: currentRide.id, latitude, longitude });
        }
      },
      null,
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
  };

  const stopLocationTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  const handleToggleOnline = async () => {
    const next = !isOnline;
    try {
      await api.put('/vtc/drivers/availability', { is_available: next });
      setIsOnline(next);
      const s = getSocket();
      if (next) {
        s?.emit('vtc-driver-online');
        startLocationTracking();
      } else {
        s?.emit('vtc-driver-offline');
        stopLocationTracking();
      }
    } catch {
      setError('Impossible de changer le statut.');
    }
  };

  const handleAcceptRide = async (ride) => {
    setActionLoading(true);
    try {
      await api.put(`/vtc/rides/${ride.id}/accept`);
      setCurrentRide({ ...ride, ride_status: 'accepted' });
      setPendingRides(prev => prev.filter(r => r.id !== ride.id));
    } catch {
      setError('Impossible d\'accepter la course.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRide = (rideId) => {
    setPendingRides(prev => prev.filter(r => r.id !== rideId));
  };

  const handleArrived = async () => {
    if (!currentRide) return;
    setActionLoading(true);
    try {
      await api.put(`/vtc/rides/${currentRide.id}/arrived`);
      setCurrentRide(r => ({ ...r, ride_status: 'arrived' }));
    } catch {
      setError('Erreur lors de la confirmation d\'arrivée.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartRide = async () => {
    if (!currentRide) return;
    setActionLoading(true);
    try {
      await api.put(`/vtc/rides/${currentRide.id}/start`);
      setCurrentRide(r => ({ ...r, ride_status: 'started' }));
    } catch {
      setError('Erreur lors du démarrage de la course.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteRide = async () => {
    if (!currentRide) return;
    setActionLoading(true);
    try {
      await api.put(`/vtc/rides/${currentRide.id}/complete`);
      setCurrentRide(null);
      await loadDriverData();
    } catch {
      setError('Erreur lors de la finalisation de la course.');
    } finally {
      setActionLoading(false);
    }
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  const vehicleLabel = { economy: 'Economy', comfort: 'Comfort', premium: 'Premium', xl: 'XL' };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'white', color: 'primary.main' }}>
                <DirectionsCarIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>Chauffeur VTC</Typography>
                <Typography variant="body2">Mon tableau de bord</Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch checked={isOnline} onChange={handleToggleOnline}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'success.main' } }} />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 12, color: isOnline ? '#22c55e' : '#6b6b6b' }} />
                  {isOnline ? 'En ligne' : 'Hors ligne'}
                </Box>
              }
              sx={{ color: 'white' }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { icon: <DirectionsCarIcon />, color: 'primary.light', value: stats.today_rides, label: "Courses aujourd'hui" },
            { icon: <AttachMoneyIcon />, color: 'success.light', value: `${fmt(stats.today_earnings)} €`, label: 'Vos gains du jour' },
            { icon: <CheckCircleIcon />, color: 'info.light', value: stats.total_rides, label: 'Total courses' },
            { icon: <Typography variant="h5" fontWeight={700}>⭐</Typography>, color: 'warning.light', value: stats.rating, label: 'Note moyenne' },
          ].map((s, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: s.color, width: 56, height: 56 }}>{s.icon}</Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                      <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {!isOnline && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<NotificationsActiveIcon />}>
            Vous êtes hors ligne. Activez le mode "En ligne" pour recevoir des courses.
          </Alert>
        )}

        {/* Course en cours */}
        {currentRide && (
          <Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip label="Course en cours" color="success" />
                <Typography variant="h6" fontWeight={700}>{fmt(currentRide.total_fare)} €</Typography>
              </Box>

              {currentRide.ride_status === 'accepted' && (
                <Alert severity="info" sx={{ mb: 2 }}>Dirigez-vous vers le point de prise en charge</Alert>
              )}
              {currentRide.ride_status === 'arrived' && (
                <Alert severity="success" sx={{ mb: 2 }}>Le passager est notifié de votre arrivée</Alert>
              )}
              {currentRide.ride_status === 'started' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>Course en cours...</Typography>
                  <LinearProgress />
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Prise en charge
                    </Typography>
                    <Typography variant="body2">{currentRide.pickup_address}</Typography>
                    <Button size="small" startIcon={<NavigationIcon />} sx={{ mt: 1 }}
                      onClick={() => openGoogleMaps(currentRide.pickup_latitude, currentRide.pickup_longitude)}>
                      Itinéraire
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Destination
                    </Typography>
                    <Typography variant="body2">{currentRide.dropoff_address}</Typography>
                    <Button size="small" startIcon={<NavigationIcon />} sx={{ mt: 1 }}
                      onClick={() => openGoogleMaps(currentRide.dropoff_latitude, currentRide.dropoff_longitude)}>
                      Itinéraire
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {currentRide.first_name} {currentRide.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{currentRide.phone}</Typography>
                </Box>
                <IconButton color="primary" href={`tel:${currentRide.phone}`}>
                  <CallIcon />
                </IconButton>
              </Box>

              {currentRide.ride_status === 'accepted' && (
                <Button fullWidth variant="contained" size="large" disabled={actionLoading} onClick={handleArrived}>
                  {actionLoading ? <CircularProgress size={20} /> : 'Je suis arrivé'}
                </Button>
              )}
              {currentRide.ride_status === 'arrived' && (
                <Button fullWidth variant="contained" color="success" size="large" disabled={actionLoading} onClick={handleStartRide}>
                  {actionLoading ? <CircularProgress size={20} /> : 'Démarrer la course'}
                </Button>
              )}
              {currentRide.ride_status === 'started' && (
                <Button fullWidth variant="contained" color="success" size="large" disabled={actionLoading} onClick={handleCompleteRide}>
                  {actionLoading ? <CircularProgress size={20} /> : 'Terminer la course'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Courses disponibles */}
        {isOnline && !currentRide && (
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>Courses disponibles</Typography>

            {pendingRides.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <DirectionsCarIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Aucune course disponible pour le moment
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {pendingRides.map((ride) => (
                  <Grid item xs={12} key={ride.id}>
                    <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {ride.first_name} {ride.last_name}
                            </Typography>
                            <Chip label={vehicleLabel[ride.vehicle_type] || ride.vehicle_type} size="small" color="primary" sx={{ mt: 1 }} />
                          </Box>
                          <Chip label={`${fmt(ride.total_fare)} €`} color="success"
                            sx={{ fontWeight: 700, fontSize: '1.1rem' }} />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Box display="flex" alignItems="start" gap={1} mb={1}>
                            <LocationOnIcon color="primary" fontSize="small" />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Prise en charge</Typography>
                              <Typography variant="body2" fontWeight={600}>{ride.pickup_address}</Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="start" gap={1}>
                            <LocationOnIcon color="error" fontSize="small" />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Destination</Typography>
                              <Typography variant="body2" fontWeight={600}>{ride.dropoff_address}</Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Chip label={`${ride.distance_km} km`} size="small" icon={<AccessTimeIcon />} sx={{ mb: 2 }} />

                        <Box display="flex" gap={2}>
                          <Button fullWidth variant="contained" disabled={actionLoading}
                            onClick={() => handleAcceptRide(ride)}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Accepter'}
                          </Button>
                          <Button fullWidth variant="outlined" onClick={() => handleRejectRide(ride.id)}>
                            Refuser
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Paiements Stripe Connect */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Paiements</Typography>
          <StripeConnectSection commissionRate={20} commissionLabel="sur le montant des courses" />
        </Box>

        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" size="large" onClick={() => navigate('/vtc/rides')}>
              Historique des courses
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VTCPartner;
